import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBell,
  FaCalendarDay,
  FaEnvelope,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaWallet,
  FaWhatsapp,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { FinanceStats } from "../components/FinancePageHead";
import { AGING_BUCKETS, agingBucketKey, emptyAging, isDueWithin } from "../utils/financeTerms";
import { mapCustomerListItemFromApi, getTenantCompanyLabel } from "../utils/formatters";
import {
  buildInvoiceShareMessage,
  buildReminderMessage,
  clientShareContext,
  emailHref,
  whatsappHref,
} from "../utils/clientInvoiceShare";
import { phoneCallLink } from "../utils/phone";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import TablePagination, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  readStoredRowsPerPage,
} from "../components/TablePagination";
import { SupportSortableHeader } from "./SupportSortableHeader";
import "../assets/css/DigixLeads.css";
import "../assets/css/AccountsReceivable.css";
import "../assets/css/ViewCustomer.css";

const ROWS_STORAGE_KEY = "financeClientsRowsPerPage";

const STATUS_OVERDUE = "overdue";
const STATUS_OUTSTANDING = "outstanding";
const STATUS_SETTLED = "settled";
const STATUS_NO_INVOICES = "none";

const STATUS_META = {
  [STATUS_OVERDUE]: { label: "Overdue", tone: "danger" },
  [STATUS_OUTSTANDING]: { label: "Owes you", tone: "warning" },
  [STATUS_SETTLED]: { label: "All paid", tone: "success" },
  [STATUS_NO_INVOICES]: { label: "No invoices", tone: "neutral" },
};

const DUE_SOON_DAYS = 7;
const CLOSED_STATUSES = new Set(["Draft", "Cancelled", "Paid"]);

const isOpenInvoice = (inv) =>
  !CLOSED_STATUSES.has(String(inv.Status || "")) && (Number(inv.Balance_Amount) || 0) > 0.001;

const emptyStats = () => ({
  invoiceCount: 0,
  unpaidCount: 0,
  balance: 0,
  overdue: 0,
  dueSoon: 0,
  total: 0,
  paid: 0,
  aging: emptyAging(),
  invoiceList: [],
});

const sumRows = (list) =>
  list.reduce(
    (acc, r) => {
      acc.unpaidCount += r.unpaidCount;
      acc.balance += r.balance;
      acc.overdue += r.overdue;
      acc.dueSoon += r.dueSoon;
      if (r.overdue > 0.001) acc.overdueClients += 1;
      AGING_BUCKETS.forEach((b) => {
        acc.aging[b.key] += r.aging[b.key];
      });
      return acc;
    },
    { ...emptyStats(), overdueClients: 0 }
  );

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function clientStatus(row) {
  if (!row.invoiceCount) return STATUS_NO_INVOICES;
  if (row.overdue > 0.001) return STATUS_OVERDUE;
  return row.balance > 0.001 ? STATUS_OUTSTANDING : STATUS_SETTLED;
}

const SORT_VALUE = {
  client: (r) => String(r.label || "").toLowerCase(),
  unpaid: (r) => r.unpaidCount,
  balance: (r) => r.balance,
  ...Object.fromEntries(AGING_BUCKETS.map((b) => [b.key, (r) => r.aging[b.key]])),
  status: (r) => STATUS_META[r.status].label,
};

const COLUMNS = [
  { id: "client", label: "Client" },
  { id: "unpaid", label: "Unpaid invoices", num: true },
  { id: "balance", label: "Owes you", num: true },
  ...AGING_BUCKETS.map((b) => ({ id: b.key, label: b.label, num: true })),
  { id: "status", label: "Status" },
];

function clientShare(t) {
  const ctx = clientShareContext(t.invoiceList, t.label);
  const hasInvoices = t.invoiceList.some((inv) => String(inv.Status || "") !== "Cancelled");
  const message = hasInvoices ? buildInvoiceShareMessage(t.invoiceList, ctx) : null;
  return {
    ctx,
    hasInvoices,
    whatsapp: message ? whatsappHref(ctx, message) : null,
    email: message ? emailHref(ctx, "Invoice details", message) : null,
    canRemind: t.unpaidCount > 0 && Boolean(ctx.phone || ctx.email),
  };
}

const unavailableTitle = (share, channel) => {
  if (!share.hasInvoices) return "No invoices to share";
  return channel === "whatsapp" ? "No phone number on this client's invoices" : "No email on this client's invoices";
};

/** Invoices / share / reminder actions — icon buttons in the table, labelled actions on cards. */
function ClientActions({ client, variant, onInvoices, onRemind }) {
  const share = clientShare(client);
  const card = variant === "card";
  const btnClass = card ? "co-maction" : "accounts-payable-action-btn";
  const disabledClass = card ? " co-maction--disabled" : " finance-tenants-action--disabled";
  const iconSize = card ? 14 : undefined;
  const label = (text) => (card ? <span>{text}</span> : null);

  const shareLink = (href, channel, Icon, text, title) =>
    href ? (
      <a
        className={`${btnClass} finance-tenants-action--${channel}`}
        href={href}
        target={channel === "whatsapp" ? "_blank" : undefined}
        rel={channel === "whatsapp" ? "noopener noreferrer" : undefined}
        title={title}
        aria-label={title}
      >
        <Icon size={iconSize} />
        {label(text)}
      </a>
    ) : (
      <span
        className={`${btnClass}${disabledClass}`}
        title={unavailableTitle(share, channel)}
        aria-label={unavailableTitle(share, channel)}
        aria-disabled="true"
      >
        <Icon size={iconSize} />
        {label(text)}
      </span>
    );

  return (
    <div className={card ? "co-mcard__action-grid" : "accounts-payable-actions-cell"}>
      <button type="button" className={btnClass} title="View invoices" aria-label="View invoices" onClick={onInvoices}>
        <FaFileInvoiceDollar size={iconSize} />
        {label("Invoices")}
      </button>
      {shareLink(share.whatsapp, "whatsapp", FaWhatsapp, "WhatsApp", "Share invoices on WhatsApp")}
      {shareLink(share.email, "email", FaEnvelope, "Email", "Share invoices by email")}
      <button
        type="button"
        className={`${btnClass} finance-tenants-action--reminder`}
        title={
          share.canRemind
            ? "Send payment reminder"
            : client.unpaidCount > 0
              ? "No phone or email on this client's invoices"
              : "Nothing outstanding"
        }
        aria-label="Send payment reminder"
        disabled={!share.canRemind}
        onClick={onRemind}
      >
        <FaBell size={iconSize} />
        {label("Remind")}
      </button>
    </div>
  );
}

/**
 * Finance → Clients: outstanding balances + links into Invoices & Payments.
 */
function FinanceTenantsTab({ onOpenInvoices }) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [sortColumn, setSortColumn] = useState("balance");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => readStoredRowsPerPage(ROWS_STORAGE_KEY));
  const [reminder, setReminder] = useState(null);
  const [reminderText, setReminderText] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [tenRes, arRes] = await Promise.all([
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
          axiosInstance.get("/fico/accounts-receivable", { params: { include_items: false } }).catch(() => ({ data: [] }))
        ]);
        const rawTenants = Array.isArray(tenRes.data) ? tenRes.data : tenRes.data?.data || [];
        const mapped = rawTenants
          .map((item) => mapCustomerListItemFromApi(item))
          .filter(Boolean);
        const arRows = Array.isArray(arRes.data) ? arRes.data : arRes.data?.data || [];
        if (!cancelled) {
          setTenants(mapped);
          setInvoices(arRows);
        }
      } catch (err) {
        console.error("Failed to load tenants finance summary:", err);
        if (!cancelled) setError(err.response?.data?.error || "Failed to load clients.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allRows = useMemo(() => {
    const byClient = new Map();
    const newestFirst = [...invoices].sort(
      (a, b) =>
        String(b.Invoice_Date || "").localeCompare(String(a.Invoice_Date || "")) ||
        Number(b.AR_Id || 0) - Number(a.AR_Id || 0)
    );
    newestFirst.forEach((inv) => {
      const cid = String(inv.client_id ?? inv.Client_Id ?? "");
      if (!cid) return;
      const prev = byClient.get(cid) || emptyStats();
      prev.invoiceList.push(inv);
      prev.invoiceCount += 1;
      prev.total += Number(inv.Total_Amount) || 0;
      prev.paid += Number(inv.Paid_Amount) || 0;
      if (isOpenInvoice(inv)) {
        const bal = Number(inv.Balance_Amount) || 0;
        const bucket = agingBucketKey(inv.Due_Date);
        prev.unpaidCount += 1;
        prev.balance += bal;
        prev.aging[bucket] += bal;
        if (bucket !== "current") prev.overdue += bal;
        if (isDueWithin(inv.Due_Date, DUE_SOON_DAYS)) prev.dueSoon += bal;
      }
      byClient.set(cid, prev);
    });

    return tenants.map((t) => {
      const stats = byClient.get(String(t.id)) || emptyStats();
      const row = { ...t, ...stats, label: getTenantCompanyLabel(t) };
      return { ...row, status: clientStatus(row) };
    });
  }, [tenants, invoices]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const getValue = SORT_VALUE[sortColumn] || SORT_VALUE.balance;
    const mult = sortDirection === "desc" ? -1 : 1;
    return allRows
      .filter((t) => statusFilter === "all" || t.status === statusFilter)
      .filter((t) => !q || String(t.label || "").toLowerCase().includes(q) || String(t.id).includes(q))
      .sort((a, b) => {
        const va = getValue(a);
        const vb = getValue(b);
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        return String(a.label).localeCompare(String(b.label));
      });
  }, [allRows, search, statusFilter, sortColumn, sortDirection]);

  const totals = useMemo(() => sumRows(rows), [rows]);
  const overall = useMemo(() => sumRows(allRows), [allRows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);
  const hasFilters = Boolean(search.trim()) || statusFilter !== "all";
  const emptyLabel = hasFilters ? "No clients match these filters." : "No clients found.";

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection(columnId === "client" || columnId === "status" ? "asc" : "desc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    try {
      localStorage.setItem(ROWS_STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  const openClient = (id) => navigate(`/tenants/${id}/view`);
  const openInvoices = (id) => onOpenInvoices && onOpenInvoices(id);

  const openReminder = (client) => {
    const ctx = clientShareContext(client.invoiceList, client.label);
    setReminder({ client, ctx });
    setReminderText(buildReminderMessage(client.invoiceList, ctx));
  };
  const closeReminder = () => setReminder(null);
  const reminderPhone = reminder ? phoneCallLink(reminder.ctx.countryCode, reminder.ctx.phone) : null;
  const reminderWhatsApp = reminder ? whatsappHref(reminder.ctx, reminderText) : null;
  const reminderEmail = reminder ? emailHref(reminder.ctx, "Payment reminder", reminderText) : null;

  return (
    <div className="accounts-payable finance-tenants-panel">
      <FinanceStats
        items={[
          {
            key: "owed",
            label: "Total owed to you",
            value: loading ? "—" : formatMoney(overall.balance),
            icon: FaWallet,
            tone: "info",
            onClick: () => setStatusFilter("all"),
          },
          {
            key: "overdue",
            label: overall.overdueClients ? `Overdue (${overall.overdueClients} clients)` : "Overdue",
            value: loading ? "—" : formatMoney(overall.overdue),
            icon: FaExclamationTriangle,
            tone: "danger",
            onClick: () => setStatusFilter(STATUS_OVERDUE),
          },
          {
            key: "due-soon",
            label: "Due this week",
            value: loading ? "—" : formatMoney(overall.dueSoon),
            icon: FaCalendarDay,
            tone: "warning",
            onClick: () => setStatusFilter(STATUS_OUTSTANDING),
          },
        ]}
      />
      <p className="finance-aging-hint">
        Amounts still unpaid, grouped by how many days past the due date they are.
      </p>
      <div className="search-actions-row accounts-payable-search-actions-row finance-tenants-toolbar">
        <div className="search-filters-row accounts-payable-toolbar-right">
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search client name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search clients"
          />
          <select
            className="search-filter-input search-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by balance status"
          >
            <option value="all">All Status</option>
            {[STATUS_OVERDUE, STATUS_OUTSTANDING, STATUS_SETTLED, STATUS_NO_INVOICES].map((key) => (
              <option key={key} value={key}>
                {STATUS_META[key].label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {error ? <div className="accounts-payable-alert">{error}</div> : null}

      <div className="accounts-payable-table-wrap co-desktop-table">
        <table className="accounts-payable-table finance-tenants-table">
          <thead>
            <tr>
              {COLUMNS.map((col, i) => (
                <th
                  key={col.id}
                  className={`accounts-payable-th-sortable${i === 0 ? " accounts-payable-sticky-first" : ""}${
                    col.num ? " finance-tenants-num" : ""
                  }`}
                >
                  <div className="accounts-payable-th-sort-row">
                    <SupportSortableHeader
                      columnId={col.id}
                      label={col.label}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </div>
                </th>
              ))}
              <th className="accounts-payable-sticky-last finance-tenants-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="accounts-payable-empty">
                  Loading clients...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="accounts-payable-empty">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              pageRows.map((t) => {
                const status = STATUS_META[t.status];
                return (
                  <tr key={t.id}>
                    <td className="accounts-payable-sticky-first">
                      <span
                        className="table-name-link"
                        role="button"
                        tabIndex={0}
                        onClick={() => openClient(t.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openClient(t.id);
                          }
                        }}
                      >
                        {t.label || `Client #${t.id}`}
                      </span>
                    </td>
                    <td className="finance-tenants-num">{t.unpaidCount}</td>
                    <td className={`finance-tenants-num${t.balance > 0.001 ? " finance-tenants-due" : ""}`}>
                      {formatMoney(t.balance)}
                    </td>
                    {AGING_BUCKETS.map((b) => (
                      <td
                        key={b.key}
                        className={`finance-tenants-num${
                          b.key !== "current" && t.aging[b.key] > 0.001 ? " finance-aging-late" : ""
                        }`}
                      >
                        {t.aging[b.key] > 0.001 ? formatMoney(t.aging[b.key]) : "—"}
                      </td>
                    ))}
                    <td>
                      <span className={`finance-tenants-status finance-tenants-status--${status.tone}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="accounts-payable-sticky-last finance-tenants-actions-col">
                      <ClientActions
                        client={t}
                        variant="table"
                        onInvoices={() => openInvoices(t.id)}
                        onRemind={() => openReminder(t)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {!loading && rows.length > 0 ? (
            <tfoot>
              <tr className="finance-tenants-totals">
                <td className="accounts-payable-sticky-first">
                  Total ({rows.length} client{rows.length === 1 ? "" : "s"})
                </td>
                <td className="finance-tenants-num">{totals.unpaidCount}</td>
                <td className="finance-tenants-num">{formatMoney(totals.balance)}</td>
                {AGING_BUCKETS.map((b) => (
                  <td key={b.key} className="finance-tenants-num">
                    {formatMoney(totals.aging[b.key])}
                  </td>
                ))}
                <td />
                <td className="accounts-payable-sticky-last" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <ResultsMobileList
        loading={loading}
        loadingLabel="Loading clients..."
        emptyLabel={emptyLabel}
        className="finance-tenants-mobile"
      >
        {!loading &&
          pageRows.map((t) => {
            const status = STATUS_META[t.status];
            return (
              <ResultsMobileCard
                key={t.id}
                title={t.label || `Client #${t.id}`}
                subtitle={t.balance > 0.001 ? `Owes you ${formatMoney(t.balance)}` : null}
                tone={
                  t.status === STATUS_OVERDUE ? "danger" : t.status === STATUS_OUTSTANDING ? "warning" : undefined
                }
                meta={[
                  {
                    label:
                      t.status === STATUS_OUTSTANDING || t.status === STATUS_OVERDUE
                        ? `${status.label} · ${t.unpaidCount} unpaid`
                        : status.label,
                    tone: status.tone,
                  },
                ]}
                fields={[
                  { label: "Owes you", value: formatMoney(t.balance) },
                  { label: "Overdue", value: formatMoney(t.overdue) },
                  { label: "Due this week", value: formatMoney(t.dueSoon) },
                  { label: "Paid so far", value: formatMoney(t.paid) },
                ]}
                onOpen={() => openClient(t.id)}
                actions={
                  <ClientActions
                    client={t}
                    variant="card"
                    onInvoices={() => openInvoices(t.id)}
                    onRemind={() => openReminder(t)}
                  />
                }
              />
            );
          })}
      </ResultsMobileList>

      {!loading && rows.length > 0 ? (
        <TablePagination
          currentPage={safePage}
          totalItems={rows.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="clients"
          rowsPerPageSelectId="finance-clients-rows-per-page"
        />
      ) : null}

      {reminder ? (
        <div className="viewlead-modal-overlay" onClick={closeReminder}>
          <div
            className="viewlead-modal-content finance-reminder-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-reminder-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title" id="finance-reminder-title">
                Payment reminder — {reminder.client.label || `Client #${reminder.client.id}`}
              </h3>
              <button type="button" className="viewlead-modal-close" onClick={closeReminder} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="finance-reminder-body">
              <p className="finance-reminder-to">
                <span>To:</span>
                {reminderPhone ? <strong>{reminderPhone.display}</strong> : null}
                {reminder.ctx.email ? <strong>{reminder.ctx.email}</strong> : null}
              </p>
              <label className="finance-reminder-label" htmlFor="finance-reminder-text">
                Message
              </label>
              <textarea
                id="finance-reminder-text"
                className="finance-reminder-text"
                value={reminderText}
                onChange={(e) => setReminderText(e.target.value)}
                rows={12}
              />
              <div className="viewlead-modal-actions finance-reminder-actions">
                {reminderWhatsApp ? (
                  <a
                    className="btn-secondary-lead finance-reminder-btn finance-reminder-btn--whatsapp"
                    href={reminderWhatsApp}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeReminder}
                  >
                    <FaWhatsapp /> Send on WhatsApp
                  </a>
                ) : null}
                {reminderEmail ? (
                  <a
                    className="btn-secondary-lead finance-reminder-btn"
                    href={reminderEmail}
                    onClick={closeReminder}
                  >
                    <FaEnvelope /> Send by email
                  </a>
                ) : null}
                <button type="button" className="btn-clear" onClick={closeReminder}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FinanceTenantsTab;
